import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { QuickAddComponent } from '@theme/quick-add';
// The rows are built from the theme's product-form-component and add-to-cart-component.
import '@theme/product-form';
import { StandardEvents } from '@shopify/events';

/** Recommendation intents in order of preference. The catalog list rendered by Liquid is the last resort. */
const INTENTS = ['complementary', 'related'];

/** The recommendations endpoint returns at most 10 products. The section trims them to the few it shows. */
const CANDIDATE_LIMIT = 10;

/** How long to wait for recommendations before settling for the catalog list. */
const LOAD_TIMEOUT = 4000;

/** How long a failed-add message stays on screen. Matches the theme's product form. */
const ERROR_DISPLAY_DURATION = 10000;

/** How long after an add settles a cart re-render may still move focus back into the list. */
const FOCUS_RESTORE_WINDOW = 2000;

/**
 * Recommendation rows already fetched, keyed by cart state. An empty string records "no
 * recommendations", which keeps the catalog list. Module-level so it outlives the element,
 * which the cart can replace when it re-renders.
 * @type {Map<string, string>}
 */
const recommendationCache = new Map();

/**
 * Set when an add started from a focused control in the list has settled. The cart re-render that
 * follows removes that control, so the next sync puts focus back on the list heading.
 */
let focusRestoreDeadline = 0;

/**
 * The cart upsell list rendered by `snippets/cirque-cart-upsell.liquid`.
 *
 * Liquid renders catalog products. This element swaps in Shopify product recommendations for
 * the first cart line (`data-product-id`), fetched through `sections/cirque-cart-upsell.liquid`.
 *
 * It does not add to the cart or refresh the cart itself. Each "Add" button and each size button
 * sits in one of the theme's `product-form-component`s, whose add dispatches the cart update that
 * `cart-items-component` answers by re-rendering the cart section, this element included. A
 * re-render puts the catalog rows back and drops `data-loaded`, which is this element's cue to
 * show recommendations again.
 *
 * @extends {Component}
 */
class CirqueCartUpsell extends Component {
  static observedAttributes = ['data-product-id', 'data-cart-products', 'data-loaded', 'aria-busy'];

  #connected = false;
  #visible = false;
  #syncQueued = false;

  /** @type {string | null} */
  #loadingSignature = null;

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #errorTimeout;

  /** Loads recommendations only once the list is on screen, so a closed drawer costs no request. */
  #intersectionObserver = new IntersectionObserver(
    (entries) => {
      this.#visible = entries[entries.length - 1]?.isIntersecting ?? false;
      if (this.#visible) this.#sync();
    },
    { rootMargin: '0px 0px 200px 0px' }
  );

  connectedCallback() {
    super.connectedCallback();
    this.#connected = true;

    this.addEventListener(StandardEvents.cartLinesUpdate, this.#handleAddStart);
    this.addEventListener(StandardEvents.cartError, this.#handleAddError);
    this.#intersectionObserver.observe(this);
    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#connected = false;
    this.#visible = false;

    this.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleAddStart);
    this.removeEventListener(StandardEvents.cartError, this.#handleAddError);
    this.#intersectionObserver.disconnect();
    this.#cancelLoad();
    clearTimeout(this.#errorTimeout);
  }

  /**
   * A cart re-render morphs this element back to its server-rendered attributes. That is the only
   * reliable signal that the rows were replaced, so every such change re-syncs.
   * @param {string} name
   * @param {string | null} oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (!this.#connected || oldValue === newValue) return;

    // These two are written by this element. Only their removal by a re-render matters.
    if ((name === 'data-loaded' || name === 'aria-busy') && newValue !== null) return;

    this.#queueSync();
  }

  /** The cart state the list was rendered for: the seed product and everything in the cart. */
  get #signature() {
    return `${this.dataset.productId ?? ''}|${this.dataset.cartProducts ?? ''}`;
  }

  /** @returns {HTMLElement | null} */
  get #list() {
    return this.querySelector('.cirque-cart-upsell__list');
  }

  /** Attribute changes arrive mid-morph, before the rows are replaced. Sync once the morph is done. */
  #queueSync() {
    if (this.#syncQueued) return;
    this.#syncQueued = true;

    queueMicrotask(() => {
      this.#syncQueued = false;
      if (this.#connected) this.#sync();
    });
  }

  /** Brings the list in line with the current cart state. Safe to call at any time. */
  #sync() {
    const signature = this.#signature;

    if (this.#loadingSignature !== null && this.#loadingSignature !== signature) this.#cancelLoad();

    if (!this.dataset.productId) {
      // Empty cart: there is no line item to base recommendations on, so the catalog list stands.
      this.removeAttribute('aria-busy');
    } else if (this.dataset.loaded !== signature) {
      const cachedRows = recommendationCache.get(signature);

      if (cachedRows !== undefined) {
        this.#show(signature, cachedRows);
      } else {
        // The stylesheet dims the catalog rows and blocks clicks until the recommendations settle.
        this.setAttribute('aria-busy', 'true');
        if (this.#visible && this.#loadingSignature !== signature) this.#load(signature);
      }
    }

    this.#restoreFocus();
  }

  /**
   * Fetches recommendations for the current cart state and shows them.
   * @param {string} signature
   */
  async #load(signature) {
    const controller = new AbortController();
    this.#abortController = controller;
    this.#loadingSignature = signature;

    let timedOut = false;
    const timeout = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, LOAD_TIMEOUT);

    /** @type {string | null} */
    let rows = null;

    try {
      // Complementary products only exist once a merchant sets them up, so ask for both lists at
      // once rather than waiting on an answer that is usually empty.
      const results = await Promise.all(INTENTS.map((intent) => this.#fetchRows(intent, controller.signal)));
      rows = results.find(Boolean) ?? '';
    } catch {
      // Cancelled because the cart changed or the element left the page: a newer sync owns the list.
      if (controller.signal.aborted && !timedOut) return;

      // Timed out or failed: fall back to the catalog list, uncached so a later render tries again.
      rows = null;
    } finally {
      clearTimeout(timeout);
    }

    if (this.#abortController !== controller) return;
    this.#abortController = null;
    this.#loadingSignature = null;

    if (rows !== null) recommendationCache.set(signature, rows);
    if (!this.#connected || this.#signature !== signature) return;

    this.#show(signature, rows ?? '');
  }

  #cancelLoad() {
    this.#abortController?.abort();
    this.#abortController = null;
    this.#loadingSignature = null;
  }

  /**
   * @param {string} intent
   * @param {AbortSignal} signal
   * @returns {Promise<string>} The rows' HTML, or an empty string when there are no recommendations.
   */
  async #fetchRows(intent, signal) {
    const { recommendationsUrl, sectionId, productId } = this.dataset;
    if (!recommendationsUrl || !sectionId || !productId) return '';

    const url = new URL(recommendationsUrl, window.location.origin);
    url.searchParams.set('section_id', sectionId);
    url.searchParams.set('product_id', productId);
    url.searchParams.set('limit', String(CANDIDATE_LIMIT));
    url.searchParams.set('intent', intent);

    const response = await fetch(url, { signal });
    if (!response.ok) return '';

    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const list = html.querySelector('.cirque-cart-upsell__list');

    return list?.querySelector('.cirque-cart-upsell__item') ? list.innerHTML : '';
  }

  /**
   * Shows the settled list: the recommendation rows, or the catalog rows already in place when
   * there are none.
   * @param {string} signature
   * @param {string} rows
   */
  #show(signature, rows) {
    const list = this.#list;

    if (rows && list) {
      // The theme's morph matches rows by id, so a row that stays keeps its element, and with it focus.
      morph(list, `<ul>${rows}</ul>`);
      this.removeAttribute('data-empty');
    }

    this.dataset.loaded = signature;
    this.removeAttribute('aria-busy');
  }

  /**
   * An add was started from one of the rows. The theme's product form dispatches this after it has
   * read its form and before it sends the request.
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleAddStart = (event) => {
    const error = this.querySelector('.cirque-cart-upsell__error');
    if (error instanceof HTMLElement) error.hidden = true;

    if (!event.promise) return;

    const form = event.target instanceof Element ? event.target : null;
    const tapped = form?.querySelector('button[type="submit"]');
    // Read before the size buttons are disabled: a browser may drop focus from a disabled button.
    const hadFocus = this.contains(document.activeElement);
    const releaseSizes = this.#holdSizes(form);

    const settle = () => {
      if (!releaseSizes) return;
      releaseSizes();

      // Disabling the tapped size can have dropped focus from it. A failed add leaves the row in
      // place, so give focus back.
      const focusLost = !document.activeElement || document.activeElement === document.body;
      if (hadFocus && focusLost && tapped instanceof HTMLElement && tapped.isConnected) tapped.focus();
    };

    // This listener runs before the cart's own, so the deadline is set before the cart re-renders.
    event.promise.then(
      () => {
        settle();
        if (hadFocus) focusRestoreDeadline = performance.now() + FOCUS_RESTORE_WINDOW;
      },
      () => settle()
    );
  };

  /**
   * Disables every size button of a product while one of its sizes is being added, so a second tap
   * cannot add again. Sold-out sizes are already disabled and are left alone.
   * @param {Element | null} form - The product form the add came from.
   * @returns {(() => void) | null} Enables the same buttons again. Null when the row has no size buttons.
   */
  #holdSizes(form) {
    const sizes = form?.closest('.cirque-cart-upsell__sizes');
    if (!sizes) return null;

    const buttons = /** @type {HTMLButtonElement[]} */ ([...sizes.querySelectorAll('button:not(:disabled)')]);
    for (const button of buttons) button.disabled = true;

    return () => {
      for (const button of buttons) button.disabled = false;
    };
  }

  /**
   * The theme's product form reports a failed add (sold out, quantity limit) with this event but
   * shows nothing for forms without its error element, so the list shows the message itself.
   * @param {import('@shopify/events').CartErrorEvent} event
   */
  #handleAddError = (event) => {
    // The catalog probably changed under us. Refetch when the cart re-renders.
    recommendationCache.clear();

    const error = this.querySelector('.cirque-cart-upsell__error');
    if (!(error instanceof HTMLElement)) return;

    const description = event.detail?.description;
    const message = typeof description === 'string' && description ? description : event.error;

    error.textContent = message || error.dataset.fallback || '';
    error.hidden = false;

    clearTimeout(this.#errorTimeout);
    this.#errorTimeout = setTimeout(() => {
      error.hidden = true;
    }, ERROR_DISPLAY_DURATION);
  };

  /** After an add removed the focused row, keeps keyboard focus in the list instead of losing it. */
  #restoreFocus() {
    if (!focusRestoreDeadline) return;

    const expired = performance.now() > focusRestoreDeadline;
    focusRestoreDeadline = 0;
    if (expired) return;

    const { activeElement } = document;
    if (activeElement && activeElement !== document.body) return;
    if (this.hasAttribute('data-empty')) return;

    const heading = this.querySelector('.cirque-cart-upsell__heading');
    if (heading instanceof HTMLElement) heading.focus();
  }
}

/**
 * "Choose options" for a product with two or more options. Reuses the theme's quick add: it fetches the
 * product page, fills the shared quick-add modal and opens it. The theme reads the product URL
 * from an enclosing product card, which the upsell rows do not have, so only that lookup changes.
 */
class CirqueUpsellQuickAdd extends QuickAddComponent {
  get productPageUrl() {
    const { productUrl } = this.dataset;

    return productUrl ? new URL(productUrl, window.location.href).toString() : '';
  }

  /**
   * Opens the quick-add modal. When the modal is not on the page (quick add is switched off in the
   * theme settings), or the click asks for a new tab, the link is left to open the product page.
   * @param {MouseEvent} event
   */
  openQuickAdd(event) {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (!document.getElementById('quick-add-dialog') || !customElements.get('quick-add-dialog')) return;

    this.handleClick(event).catch(() => {
      window.location.assign(this.productPageUrl);
    });
  }
}

if (!customElements.get('cirque-upsell-quick-add-component')) {
  customElements.define('cirque-upsell-quick-add-component', CirqueUpsellQuickAdd);
}

if (!customElements.get('cirque-cart-upsell-component')) {
  customElements.define('cirque-cart-upsell-component', CirqueCartUpsell);
}
